import { miniApp } from "@telegram-apps/sdk";
import { useStore } from "zustand";
import { createStore } from "zustand/vanilla";
import { reportClientFault } from "./client-report";

/* ------------------------------------------------------------------ *
 * Mobile lifecycle + reconnection.
 *
 * Telegram Mini Apps live inside a mobile WebView: a screen lock or an app
 * switch suspends JS timers and silently kills WebSockets, and iOS is happy to
 * keep a HALF-OPEN socket that `readyState` still reports as OPEN. This module
 * is the ONE fused activity source (visibilitychange + pageshow/pagehide +
 * Chromium freeze/resume + Telegram's `miniApp.isActive`) and the registry of
 * every live realtime connection. On hidden it suspends them (no teardown — a
 * short lock must not cost the seat); on visible it health-probes each one (WS
 * readyState, then an app-level ping/pong round trip with a hard deadline) and
 * reconnects the dead ones with exponential backoff + jitter, driving the
 * `ConnectionOverlay` UI. Connectors register on connect and unregister on
 * dispose (see game-connection.ts / colyseus.ts / online-connection.ts /
 * presence.ts, and the `silent` chat sockets in dm.ts / table-chat.ts).
 * ------------------------------------------------------------------ */

/**
 * How long the client keeps AUTO-retrying a dropped game socket before giving up.
 *
 * ⚠️ **THIS IS A FLOOR OVER THE SERVER'S GRACE, NOT A MIRROR OF IT — DO NOT "SYNC" IT DOWN.**
 * The invariant is `RECONNECT_WINDOW_MS + SERVER_GRACE_MARGIN_MS ≥ the server's
 * RECONNECT_WINDOW_SECONDS`, and the two are deliberately unequal: the server's grace was
 * shortened to 60s on 2026-09-01 while this stayed at 120. That is not drift. A seat whose grace
 * lapses is handed to a bot but keeps a MANUAL reservation that never expires, so a client still
 * retrying afterwards RECLAIMS the chair — measured on the running server, a reconnect a full ten
 * seconds past the expiry landed on the original seat. Retrying longer than the server holds the
 * chair is therefore pure upside; retrying for LESS is what strands people.
 *
 * (It used to stop after ~6 attempts (~24s) while the server held the seat far longer, so a
 * returning player found their seat already botted with no way back in the app.)
 */
export const RECONNECT_WINDOW_MS = 120_000;
/**
 * How long the client outlives the server's seat grace.
 *
 * The two windows do NOT start together: the client's begins when the socket dies, the
 * server's only when `onLeave` fires — after the WS ping gives up (`pingInterval 3000 ×
 * pingMaxRetries 2` ≈ 9s). Measured equal, the CLIENT therefore quit first and a returning
 * player met a dead end while the server was still holding their seat. The client must
 * always be the last to give up.
 */
export const SERVER_GRACE_MARGIN_MS = 15_000;
/**
 * How long a reconnect may take before the user is told anything.
 *
 * A brief background→foreground cycle usually recovers in well under a second. Painting an
 * overlay for that flashes an alarming message that is immediately superseded — the stacked
 * «قطع شد» → «در حال اتصال دوباره» the operator reported. Under this threshold the user sees
 * NOTHING; over it they see exactly one calm notice.
 */
export const RECONNECT_NOTICE_DELAY_MS = 1500;
/** Hard cap on auto-retry attempts — a backstop against a runaway loop; the real limit
 *  is {@link RECONNECT_WINDOW_MS}. Backoff caps at 8s, so ~120s ≈ 17 attempts; 60 is
 *  comfortably above that and never reached in practice. */
export const MAX_RECONNECT_ATTEMPTS = 60;
/** First backoff delay (doubles per attempt). */
export const BACKOFF_BASE_MS = 500;
/** Backoff ceiling. */
export const BACKOFF_CAP_MS = 8000;
/** Jitter span (±30%) so a room's players don't reconnect in lockstep. */
export const BACKOFF_JITTER = 0.3;
/** How long the app-level ping waits for its pong before declaring the socket
 *  dead (catches iOS half-open sockets whose readyState is still OPEN). */
export const PING_TIMEOUT_MS = 3000;

/**
 * How long ONE reacquire attempt may take before it is abandoned and retried.
 *
 * ⚠️ WITHOUT THIS, A SINGLE ATTEMPT COULD SWALLOW THE ENTIRE WINDOW. The retry loop checks its
 * budget only BETWEEN attempts, so an `await reacquire()` that never settles blocks everything —
 * observed in production: `conn=presence attempts=1 elapsedMs=858792 windowMs=135000 reason=budget`,
 * i.e. ONE attempt for 14 minutes where backoff predicts ~17. The probe has always been bounded
 * ({@link PING_TIMEOUT_MS}); the reacquire never was.
 *
 * ⚠️ IT MUST NOT EQUAL THE SERVER'S SEAT-RESERVATION WINDOW, AND IT USED TO.
 *
 * Colyseus mints a seat reservation valid for `COLYSEUS_SEAT_RESERVATION_TIME` and refuses the WS
 * upgrade once it lapses («seat reservation expired», thrown from `WebSocketServer.completeUpgrade`).
 * This bound was once ALSO 15s, the window's then value, so the client abandoned an attempt at the
 * very instant the reservation it was consuming died: every retry raced a full-length window it was
 * also guaranteed to lose — seven of them is 105 s of the 135 s budget spent WAITING, exactly the
 * shape of the production beacons of the time (`attempts=7 elapsedMs=135001 hiddenMs=0`). The window
 * is **30 s** since 2026-09-23 (`infra/docker-compose.yml`), and `deploy-safety.test.ts` keeps this
 * bound at most two thirds of it, so an abandoned attempt still leaves a retry a live window.
 *
 * ⚠️ AND THEN IT WAS SIZED FROM THE HEALTHY PATH, WHICH IS NOT A BOUND. It was 8 s: «a healthy join is
 * ~17 ms, a 25-way concurrent burst at 142 live players peaked at 714 ms, so 8 s is over 11× the worst
 * observed case». That is a floor for detecting a HANG, and it says nothing about what a real client
 * needs — the population this exists for is measured at p50 18,837 ms to establish a connection, and
 * 163 of the 208 prod give-ups (14 d) were `conn=presence` cut at exactly this bound. Reproduced on
 * staging against presence (`apps/web/tools/reconnect-probes/probe-presence-bound.cjs`, 2 trials an
 * arm, the socket severed with the latency already applied):
 *
 *     added latency   one fresh join takes    at the 8 s bound
 *     0 ms            ~0.2 s                  CONNECTED 2/2, 1 attempt
 *     5,000 ms        ~10 s (POST, upgrade)   GAVE UP 0/2 — 10-11 attempts, every one cut; the
 *                                             beacon is prod's row to the digit (attempts=10
 *                                             elapsedMs=135000 reason=budget … within 8000ms)
 *     10,000 ms       ~20 s                   GAVE UP 0/2
 *
 * Each cut attempt's socket DID land — its JOIN arrived — and the disposer closed it in the same
 * instant, so what the bound was destroying was a working connection, ten times over.
 *
 * 18 s lets the 5 s path finish (pinned ≥ 12 s) and stays inside ⅔ of the 30 s window (pinned).
 * ⚠️ IT DOES NOT RESCUE THE 10 s PATH, AND NO CLIENT BOUND CAN: the game-server's own transport ping
 * (`pingInterval 3000 × pingMaxRetries 2`, `server.ts`) terminates a socket that has not answered
 * within ~6–9 s, and a client whose handshake response is still 10 s in flight cannot answer at all —
 * measured on the online table, where the reclaim landed and the ping killed it 8.5 s later.
 *
 * The 858s hang this bound exists to prevent is unaffected; it is still cut.
 */
export const REACQUIRE_TIMEOUT_MS = 18_000;

/**
 * THE SAME BOUND, FOR THE ONE DOOR WHOSE RESERVATION IS NOT THE FRESH-JOIN WINDOW.
 *
 * WHY THIS DOOR MAY HAVE A LONGER ONE. `online-connection.ts` reacquires with
 * `client.reconnect(token)`, and `_reserveSeat(…, allowReconnection = true)` does NOT arm the
 * `seatReservationTime` timeout (`@colyseus/core` `Room.js:488`). Mid-game that reservation is
 * `allowReconnection(client, "manual")` and never lapses on its own; what the server's 60 s drop
 * GRACE decides is when a bot takes the chair. So the ceiling here is two thirds of the grace — a
 * slow retry should still land while the chair is the player's — not of the fresh-join window.
 * Every other connector (`presence`, `table-chat`, `dm`, solo) reacquires with `joinOrCreate` and
 * keeps {@link REACQUIRE_TIMEOUT_MS} — see `deploy-safety.test.ts`, which pins both ranges.
 *
 * WHAT 25 s WAS MEASURED AGAINST — the BROKEN path, on staging, two trials per arm
 * (`apps/web/tools/reconnect-probes/probe-reacquire-bound.cjs`; «connected» = the table's own
 * `resumed`, held 10 s with no second drop):
 *
 *     added latency    reclaim lands     at the 8 s bound       at 25 s
 *     0 ms                    ~0 s       CONNECTED 2/2          CONNECTED 2/2 (×2 runs)
 *     5,000 ms               ~10 s       SEAT EXPIRED 0/2       CONNECTED 2/2 (×2 runs)
 *     10,000 ms              ~10 s       0/2                    SEAT EXPIRED 0/2 — see below
 *
 * ⚠️ THE 10 s ROW WAS FIRST REPORTED «CONNECTED 2/2», AND IT WAS NEVER TRUE. The probe of the time
 * scored a CREATED WebSocket, which is the upgrade being SENT; the reclaim then did land (+30 s),
 * and the game-server's transport ping (`pingInterval 3000 × pingMaxRetries 2`) killed it 8.5 s
 * later — a client whose handshake response is still 10 s in flight cannot pong. Every retry then
 * carried the pre-reclaim token and was refused, terminally. No client bound reaches that.
 *
 * ⚠️ AND THE COST OF BEING TOO SHORT IS NOT A MISSED RECONNECT, IT IS THE SEAT. Measured on the
 * staging access log, identically in both 5 s trials: attempt 1 is granted its reservation, is
 * abandoned at 8 s, and its connect then COMPLETES at ~10 s — consuming the one reconnection
 * reservation and reclaiming the chair — whereupon the disposer tears that working socket down.
 * Every later attempt is refused (`92`-byte body) and the cycle ends on «جات به ربات سپرده شد».
 */
export const ONLINE_REACQUIRE_TIMEOUT_MS = 25_000;

/**
 * HOW LONG A REFUSED RECONNECT MEANS «NOT YET» RATHER THAN «SEAT GONE» — counted from the FIRST
 * refusal of an outage (see {@link ReconnectTooEarlyError}).
 *
 * Colyseus answers a `reconnect` it holds no reservation for with `4214 reconnection token invalid
 * or expired`, and the reservation only exists once the room's `onLeave` has run — once the SERVER
 * has noticed the drop. A socket that closed with a close frame is noticed at once; one that simply
 * went silent only when the game-server's transport ping gives up (`pingInterval 3000 ×
 * pingMaxRetries 2`, `server.ts`): 3–9 s after the silence began. A client that learns of the drop
 * FIRST — its own socket closed at once, or it tore the socket down and POSTed in the same instant —
 * is refused inside that gap while the seat is still its own. Prod, 14 days to 2026-09-27: 1,853
 * such refusals, each painted «جات به ربات سپرده شد» and STOPPED the cycle while the server armed the
 * full grace a moment later. Reproduced on staging, both shapes:
 * `apps/web/tools/reconnect-probes/probe-early-reconnect.cjs`.
 *
 * 12 s = the server's worst case (9 s) + one retry + a round trip, pinned against `server.ts` by
 * `deploy-safety.test.ts`. Past it a 4214 is the answer again: a seat that is genuinely gone (the
 * host removed it, the match is over) still gets the real screen — this much later.
 */
export const RECONNECT_TOO_EARLY_WINDOW_MS = 12_000;
/** How soon a «not yet» refusal is retried. The release it waits for is seconds away; the growing
 *  backoff (4 s, then 8 s, by then) would leave the player waiting well past it. */
export const RECONNECT_TOO_EARLY_RETRY_MS = 1_000;
/**
 * How long the online table's reconnect waits for ITS OWN teardown's close to land before it POSTs.
 *
 * DON'T RACE YOUR OWN CLOSE. `manageRoom`'s reconnect closes the old socket — a close frame, which
 * is what makes the server let go of the seat — and used to POST the reconnect in the same instant
 * over another connection. The POST got there first and was refused 4214: 1,200 of the 1,853
 * refusals above saw the old socket close in that same second. Waiting for the close handshake to
 * complete (the server's own close frame coming back) puts the reservation in place before the
 * request that asks for it. Bounded, because a socket condemned by a failed probe may have a dead
 * path, and its close then never lands — in which case the server cannot have let go yet anyway,
 * and {@link RECONNECT_TOO_EARLY_WINDOW_MS} carries the attempt instead.
 */
export const ONLINE_CLOSE_WAIT_MS = 1_500;

/** How often the freeze probe ticks while a reconnect cycle runs. */
export const FREEZE_PROBE_MS = 1000;
/**
 * A gap between two probe ticks above this is not scheduler jitter — the JS context was SUSPENDED.
 *
 * Generous on purpose: a busy main thread, a throttled background tab and a slow device all delay
 * a 1s timer by hundreds of milliseconds, and mistaking that for a freeze would credit real
 * retry time back to the budget. Multi-second gaps only happen when the context actually stopped.
 */
export const FREEZE_GAP_MS = 5000;

/** Thrown when a reacquire attempt exceeds {@link REACQUIRE_TIMEOUT_MS}. Retryable, like any
 *  network failure — deliberately NOT a {@link SeatExpiredError}, which is terminal. */
export class ReacquireTimeoutError extends Error {
  constructor(name: string, ms: number) {
    super(`${name}: reacquire did not complete within ${ms}ms`);
    this.name = "ReacquireTimeoutError";
  }
}

/**
 * Bound a reacquire, and hand anything it produces after we stopped waiting to `late`.
 *
 * Abandoning the promise is not enough: a hung connect that resolves late would hand us a second,
 * live, unmanaged socket that nobody ever leaves — trading a hang for a leak. So the abandoned
 * promise keeps `late` attached, and the CALLER decides what the arrival is.
 *
 * ⚠️ IT USED TO BE A DISPOSER, AND THAT COST SEATS. A late arrival is usually not a leak but the
 * reconnect we were waiting for, landing a moment after the path came back — prod, 2026-09-26
 * 22:54:51: the server reclaimed the seat, sent the table, and this closed it 0.8 s later (a second
 * drop, a second bot takeover, a rotated token the next attempt could not use). {@link manageRoom}
 * now ADOPTS it whenever nothing else holds the connection, and closes only a duplicate or one the
 * player has walked away from.
 */
export function boundedReacquire<R>(
  attempt: Promise<R>,
  name: string,
  late: (room: R) => void,
  ms = REACQUIRE_TIMEOUT_MS,
): Promise<R> {
  let settled = false;
  return new Promise<R>((resolve, reject) => {
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      // We are no longer waiting for it — but it may still arrive. Whatever it brings goes to `late`.
      attempt.then(late).catch(() => undefined);
      reject(new ReacquireTimeoutError(name, ms));
    }, ms);
    attempt.then(
      (room) => {
        if (settled) return; // already timed out: `late` above owns this room
        settled = true;
        clearTimeout(timer);
        resolve(room);
      },
      (error: unknown) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        reject(error instanceof Error ? error : new Error(describeConnectionError(error)));
      },
    );
  });
}

/**
 * A human-readable one-liner for whatever a transport hands us.
 *
 * A WebSocket failure arrives as a DOM **event**, not an Error, so the beacon's
 * `String(lastError)` rendered the useless `[object ProgressEvent]` — which is exactly the field
 * meant to discriminate why a reconnect died. Pull the parts that identify it instead.
 */
export function describeConnectionError(error: unknown): string {
  if (error instanceof Error) return `${error.name}: ${error.message}`;
  if (typeof error === "string") return error || "none";
  if (error === null || error === undefined) return "none";
  const e = error as {
    type?: unknown;
    code?: unknown;
    reason?: unknown;
    wasClean?: unknown;
    message?: unknown;
    target?: { readyState?: unknown; url?: unknown };
  };
  if (typeof e.type === "string") {
    const bits = [`event=${e.type}`];
    // CloseEvent carries the only genuinely diagnostic fields a socket failure has.
    if (typeof e.code === "number") bits.push(`code=${e.code}`);
    if (typeof e.reason === "string" && e.reason) bits.push(`reason=${e.reason}`);
    if (typeof e.wasClean === "boolean") bits.push(`clean=${e.wasClean}`);
    if (typeof e.target?.readyState === "number") bits.push(`readyState=${e.target.readyState}`);
    if (typeof e.target?.url === "string") bits.push(`url=${hostOf(e.target.url)}`);
    return bits.join(" ");
  }
  if (typeof e.message === "string" && e.message) return e.message;
  try {
    return JSON.stringify(error) ?? "unknown";
  } catch {
    return "unknown";
  }
}

/** Host only — a socket URL carries the initData query string, which must never reach a log. */
function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return "?";
  }
}
/** Activity debounce — the resume path fires bursts of signals (visibility +
 *  pageshow + Telegram activation); only a value stable this long applies. */
export const ACTIVITY_DEBOUNCE_MS = 150;

/** Thrown by a connection's `reconnect()` when the seat is unrecoverable (the
 *  reconnection window expired / the room is gone) — the cycle stops retrying
 *  and the overlay shows its "seat expired" state instead of "failed". */
export class SeatExpiredError extends Error {
  /** True when the table had NOT started when we lost it (waiting room /
   *  team-select) — there is no bot takeover pre-start, so the overlay must
   *  say "the seat was freed", never "a bot replaced you". */
  readonly preStart: boolean;

  constructor(message = "seat expired", preStart = false) {
    super(message);
    this.name = "SeatExpiredError";
    this.preStart = preStart;
  }
}

/**
 * Thrown by a connection's `reconnect()` when the server refused it only because it has not yet
 * REGISTERED the drop: the seat is still ours, its reservation just does not exist yet (see
 * {@link RECONNECT_TOO_EARLY_WINDOW_MS}). Retryable, like any network failure — deliberately NOT a
 * {@link SeatExpiredError} — and retried after `retryInMs` rather than on the growing backoff,
 * because what it waits for is a server-side event seconds away.
 */
export class ReconnectTooEarlyError extends Error {
  readonly retryInMs: number;

  constructor(message: string, retryInMs: number) {
    super(message);
    this.name = "ReconnectTooEarlyError";
    this.retryInMs = retryInMs;
  }
}

/** What the ConnectionOverlay renders. */
export type OverlayState =
  | { kind: "none" }
  | { kind: "reconnecting"; attempt: number; maxAttempts: number }
  | { kind: "failed" }
  | { kind: "seatExpired"; preStart?: boolean };

export interface LifecycleState {
  /** The fused, debounced "the app is on screen and running" boolean. */
  appActive: boolean;
  /** Epoch ms of the last active→hidden transition (null while active). */
  hiddenAt: number | null;
  overlay: OverlayState;
}

/** Per-connection reconnect state the lifecycle reports through
 *  {@link ManagedConnection.onStatus}: a cycle started an attempt
 *  (`reconnecting`), the connection is provably back (`connected`), or the
 *  cycle exhausted its attempts (`failed` — manual retry only). */
export type ManagedConnectionStatus = "reconnecting" | "connected" | "failed";

/** The hooks a live realtime connection registers (one per connector). */
export interface ManagedConnection {
  /** Diagnostic tag for logs, e.g. `"solo:hokm"`. */
  name: string;
  /** Resolve true iff the connection is provably alive: WS OPEN **and** the
   *  app-level ping echoed within {@link PING_TIMEOUT_MS} (see {@link probeRoom}). */
  probe(): Promise<boolean>;
  /** Tear the dead socket down and re-establish (rejoin / colyseus reconnect).
   *  Reject to retry with backoff; reject {@link SeatExpiredError} to stop. */
  reconnect(): Promise<void>;
  /** The app went hidden — stop assuming liveness. NO teardown: a short lock
   *  must not cost the seat; the socket may well survive. */
  suspend(): void;
  /** Background/best-effort connection (presence, chat): reconnects silently
   *  and never drives the overlay. */
  silent?: boolean;
  /** Observes THIS connection's own reconnect state. The full-screen overlay
   *  reports the same transitions app-wide for non-silent (game) connections;
   *  `silent` ones that still have UI (the chat surfaces) use this to drive
   *  their own inline notice instead. */
  onStatus?: (status: ManagedConnectionStatus) => void;
}

/** Backoff before retry `attempt` (1-based): 0.5s doubling to the 8s cap, with
 *  ±30% jitter. Exported pure so the schedule is directly testable. */
export function backoffDelayMs(attempt: number, random: () => number = Math.random): number {
  const base = Math.min(BACKOFF_BASE_MS * 2 ** (attempt - 1), BACKOFF_CAP_MS);
  const jitter = 1 + (random() * 2 - 1) * BACKOFF_JITTER;
  return Math.round(base * jitter);
}

/** What a connector needs from the lifecycle — structural, so tests can inject
 *  a recording fake instead of the singleton. */
export interface ConnectionRegistry {
  register(conn: ManagedConnection): () => void;
  reportDead(conn: ManagedConnection): void;
  /**
   * A reconnect the cycle had already ABANDONED landed and was adopted (see {@link manageRoom}):
   * the connection is live again. End a running cycle as a success at its next checkpoint, and
   * take down a «failed» / «seat expired» overlay a finished one left — we are holding the seat.
   */
  reportRecovered(conn: ManagedConnection): void;
}

/* ------------------------------------------------------------------ *
 * Socket helpers shared by every connector
 * ------------------------------------------------------------------ */

/** The structural subset of a colyseus.js `Room` the health/teardown helpers
 *  need — fakeable in tests with a plain object (no WebSocket). */
export interface ProbeableRoom {
  connection?: { isOpen: boolean };
  send(type: string, message?: unknown): void;
  /** colyseus.js returns the handler's unsubscribe function. */
  onMessage(type: string, callback: (message: unknown) => void): unknown;
}

let pingSeq = 0;

/**
 * The health probe: dead when the WS is not OPEN; otherwise an app-level
 * `"ping"` → `"pong"` round trip (every game/presence room echoes it) with a
 * {@link PING_TIMEOUT_MS} deadline — an iOS half-open socket stays OPEN but
 * never echoes, and MUST read as dead. Never rejects.
 */
export function probeRoom(room: ProbeableRoom, timeoutMs = PING_TIMEOUT_MS): Promise<boolean> {
  if (!room.connection?.isOpen) return Promise.resolve(false);
  return new Promise((resolve) => {
    const nonce = ++pingSeq;
    let off: (() => void) | undefined;
    let settled = false;
    const finish = (alive: boolean): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      off?.();
      resolve(alive);
    };
    const timer = setTimeout(() => finish(false), timeoutMs);
    try {
      const unsubscribe = room.onMessage("pong", (echoed) => {
        // Any pong at/after our nonce proves the round trip (stray older pongs
        // from a previous probe must not vouch for this one).
        if (typeof echoed !== "number" || echoed >= nonce) finish(true);
      });
      off = typeof unsubscribe === "function" ? (unsubscribe as () => void) : undefined;
      room.send("ping", nonce);
    } catch {
      finish(false);
    }
  });
}

/** A room leave that can NEVER throw or block — exits must always work. The
 *  non-consented close (plain socket close, no LEAVE_ROOM frame) is used for
 *  teardown-before-reconnect so the server keeps the seat reserved. The returned promise
 *  settles when the leave does (colyseus.js resolves it from the socket's close) and never
 *  rejects; callers that do not care simply drop it. */
export function safeRoomLeave(
  room: { leave(consented?: boolean): Promise<unknown> },
  consented = true,
): Promise<void> {
  try {
    return room.leave(consented).then(
      () => undefined,
      () => undefined,
    );
  } catch {
    // a dying transport may throw synchronously — exiting must still proceed
    return Promise.resolve();
  }
}

/** `room.send` that swallows a dying transport's synchronous throw (sending
 *  into a CONNECTING/CLOSED WebSocket can raise). Lost frames are fine — the
 *  server re-emits full state on the next (re)join. */
export function safeSend(room: ProbeableRoom, type: string, message?: unknown): void {
  try {
    room.send(type, message);
  } catch (error) {
    console.error(`[lifecycle] send "${type}" failed on a dead socket`, error);
  }
}

/** Clean teardown of a dead room before reconnecting: drop every listener (so
 *  the zombie can't race the fresh room's handlers) and close non-consented
 *  (the server's reconnection window keeps the seat).
 *
 *  Resolves when that close has LANDED — the close handshake completed — or at once when the
 *  socket was not open, so there is nothing to land: colyseus.js resolves a `leave` from the
 *  socket's `onclose`, which a socket that has already closed never fires again, so waiting on it
 *  unconditionally would hang. Never rejects. (See {@link ONLINE_CLOSE_WAIT_MS} for who waits.) */
export function teardownRoom(room: {
  removeAllListeners?: () => void;
  leave(consented?: boolean): Promise<unknown>;
  connection?: { isOpen: boolean };
}): Promise<void> {
  const open = room.connection?.isOpen === true;
  try {
    room.removeAllListeners?.();
  } catch {
    // already destroyed
  }
  const closed = safeRoomLeave(room, false);
  return open ? closed : Promise.resolve();
}

/** Resolves when `p` settles or `ms` have passed, whichever comes first. Never rejects. */
function settleWithin(p: Promise<unknown>, ms: number): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    const done = (): void => {
      clearTimeout(timer);
      resolve();
    };
    p.then(done, done);
  });
}

/* ------------------------------------------------------------------ *
 * The lifecycle engine
 * ------------------------------------------------------------------ */

interface Entry {
  conn: ManagedConnection;
  /** The in-flight reconnect cycle (awaited before a fresh sweep re-probes). */
  cycle: Promise<void> | null;
  /** Collapses the cycle's current backoff sleep (visibility flipped). */
  wake: (() => void) | null;
  /** Terminal until the overlay's manual retry / a fresh visible sweep. */
  failed: boolean;
  /** The seat is gone for good — never probed or reconnected again. */
  expired: boolean;
  /** Milliseconds of the CURRENT cycle spent backgrounded. The retry budget is wall
   *  clock, so this is what tells us whether a window was really spent retrying or
   *  merely elapsed while the phone was asleep. */
  hiddenMs: number;
  /** When the app last went inactive during this cycle (null = currently active). */
  hiddenSince: number | null;
  /** A late reconnect was adopted while the cycle ran — it ends, as a success, at its next
   *  checkpoint (see {@link ConnectionLifecycle.reportRecovered}). */
  recovered: boolean;
}

export interface LifecycleDeps {
  /** Jitter source (injected in tests for exact schedules). */
  random?: () => number;
  debounceMs?: number;
  /** Telegram activity source; the default subscribes `miniApp.isActive`,
   *  guarded — outside Telegram / before init() it just no-ops. */
  subscribeTelegramActive?: (cb: (active: boolean) => void) => (() => void) | undefined;
  now?: () => number;
}

function defaultTelegramActive(cb: (active: boolean) => void): (() => void) | undefined {
  try {
    // Signals work without init(); if the SDK/bridge is unavailable this throws
    // and the DOM sources carry the whole signal alone.
    return miniApp.isActive.sub(() => {
      try {
        cb(miniApp.isActive());
      } catch {
        // signal read failed — keep the last known value
      }
    });
  } catch {
    return undefined;
  }
}

export class ConnectionLifecycle {
  readonly store = createStore<LifecycleState>(() => ({
    appActive: true,
    hiddenAt: null,
    overlay: { kind: "none" },
  }));

  private readonly entries = new Set<Entry>();
  private readonly random: () => number;
  private readonly debounceMs: number;
  private readonly now: () => number;
  private readonly telegramSource: LifecycleDeps["subscribeTelegramActive"];

  /** Raw (undebounced) inputs the fused signal derives from. */
  private frozen = false;
  private pageHidden = false;
  private tgActive: boolean | null = null;
  /** A bfcache restore / forced sweep is owed even if `appActive` never flipped. */
  private forceSweep = false;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  /** Bumps on every activity flip; stale cycles/sweeps check it and stop. */
  private gen = 0;

  constructor(deps: LifecycleDeps = {}) {
    this.random = deps.random ?? Math.random;
    this.debounceMs = deps.debounceMs ?? ACTIVITY_DEBOUNCE_MS;
    this.now = deps.now ?? Date.now;
    this.telegramSource = deps.subscribeTelegramActive ?? defaultTelegramActive;
  }

  /** The current fused activity value. */
  get appActive(): boolean {
    return this.store.getState().appActive;
  }

  subscribe(listener: (state: LifecycleState) => void): () => void {
    return this.store.subscribe(listener);
  }

  /** Attach the fused activity sources (call once at boot). Returns a detach. */
  start(): () => void {
    const evaluate = (): void => {
      // A VISIBLE DOCUMENT IS ON SCREEN — whatever an earlier `pagehide`/`freeze`/Telegram
      // deactivation said. See {@link noteReturn}.
      if (typeof document !== "undefined" && document.visibilityState === "visible") {
        this.noteReturn();
      }
      this.evaluate();
    };
    const onPageShow = (event: Event): void => {
      this.noteReturn();
      // A bfcache restore resumed a page whose sockets are long dead — force a
      // health sweep even though visibility may never have flipped.
      if ((event as PageTransitionEvent).persisted) this.forceSweep = true;
      this.evaluate();
    };
    const onPageHide = (): void => {
      this.pageHidden = true;
      this.evaluate();
    };
    const onFreeze = (): void => {
      this.frozen = true;
      this.evaluate();
    };
    const onResume = (): void => {
      this.noteReturn();
      // Chromium thawed us from the frozen state — sockets may have been culled.
      this.forceSweep = true;
      this.evaluate();
    };

    document.addEventListener("visibilitychange", evaluate);
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("pagehide", onPageHide);
    // Chromium Page Lifecycle (Android WebView) — never fires elsewhere.
    document.addEventListener("freeze", onFreeze);
    document.addEventListener("resume", onResume);
    const offTelegram = this.telegramSource?.((active) => {
      this.tgActive = active;
      if (active) {
        // Telegram says its Mini App is foregrounded — that is evidence for the DOM
        // latches too, not just for its own.
        this.noteReturn();
        this.forceSweep = true;
      }
      this.evaluate();
    });

    return () => {
      document.removeEventListener("visibilitychange", evaluate);
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("pagehide", onPageHide);
      document.removeEventListener("freeze", onFreeze);
      document.removeEventListener("resume", onResume);
      offTelegram?.();
      if (this.debounceTimer !== null) clearTimeout(this.debounceTimer);
    };
  }

  /** Register a live connection; returns the unregister (call on dispose). */
  register(conn: ManagedConnection): () => void {
    const entry: Entry = {
      conn,
      cycle: null,
      wake: null,
      failed: false,
      expired: false,
      hiddenMs: 0,
      hiddenSince: null,
      recovered: false,
    };
    this.entries.add(entry);
    return () => {
      if (!this.entries.delete(entry)) return;
      entry.wake?.(); // collapse any pending backoff so the cycle unwinds
      // Its overlay is meaningless once the owner is gone (the container
      // unmounted / the connector was disposed) — clear it.
      if (!conn.silent) this.setOverlay({ kind: "none" });
    };
  }

  /** A connector noticed its socket dropped while the app is VISIBLE (an
   *  onLeave without consent) — reconnect right away, no probe needed. */
  reportDead(conn: ManagedConnection): void {
    if (!this.appActive) return; // suspended — the resume sweep will handle it
    const entry = this.entryOf(conn);
    if (!entry || entry.cycle || entry.expired) return;
    entry.cycle = this.runCycle(entry, this.gen).finally(() => {
      entry.cycle = null;
    });
  }

  /**
   * A reconnect the cycle had ABANDONED landed and {@link manageRoom} adopted it — the connection
   * is live, whatever the cycle last concluded.
   *
   * That includes `expired`: a refusal can reach the phone BEFORE the reclaim that caused it (the
   * earlier attempt's upgrade took the seat and rotated the token, so the later attempt's request
   * was refused as seat-gone, and the two responses race across a network that has just come
   * back). The seat is demonstrably ours — we hold a socket in it — so «جات به ربات سپرده شد» is
   * taken down rather than left over a live table.
   */
  reportRecovered(conn: ManagedConnection): void {
    const entry = this.entryOf(conn);
    if (!entry) return; // unregistered: its owner is gone, and nothing may be painted for it
    entry.expired = false;
    entry.failed = false;
    // A running cycle ends at its next checkpoint — never with another attempt, which would start
    // by closing the room just adopted. (A cycle that starts later resets this.)
    entry.recovered = true;
    entry.wake?.();
    // The UI is settled NOW, not at that checkpoint: the connection is up this instant.
    if (!conn.silent) {
      const kind = this.store.getState().overlay.kind;
      if (kind === "reconnecting" || kind === "failed" || kind === "seatExpired") {
        this.setOverlay({ kind: "none" });
      }
    }
    this.notifyStatus(entry, "connected");
  }

  /** The overlay's manual retry (after a failed cycle): fresh probe→cycle for
   *  every failed connection. */
  retry(): void {
    for (const entry of this.entries) {
      if (!entry.failed || entry.expired) continue;
      entry.failed = false;
      void this.checkEntry(entry, this.gen);
    }
  }

  /** Clear the overlay (its exit/back-to-lobby buttons). */
  dismiss(): void {
    this.setOverlay({ kind: "none" });
  }

  /**
   * Last-resort handler for an uncaught error / unhandled rejection while a game is open.
   *
   * It REPORTS and NOTHING ELSE. It used to paint the connection-failure overlay, and on
   * 2026-07-31 that trapped two live players: an iOS audio-context rejection
   * («InvalidStateError: Failed to start the audio device», captured by the beacon) was
   * rendered as «اتصال برقرار نشد» over a perfectly healthy socket. The retry button is
   * inert on this path — `retry()` only acts on entries carrying `failed`, which only
   * {@link runCycle} sets — so the modal could not be dismissed, it swallowed every tap at
   * z-70, and the player sat at the table as a ghost the server still counted as present.
   *
   * THE RULE THIS ENCODES: never assert a connection failure we have not verified. The
   * overlay speaks for the connection subsystem; an error from anywhere else does not get
   * to speak in its voice. Suppressing iOS's fatal error sheet — the reason this exists —
   * is done by `preventDefault()` in {@link installGlobalErrorRouting}, which is unaffected.
   */
  reportCrash(reason?: unknown): void {
    reportClientFault("crash-router", {
      reason: reason instanceof Error ? reason : String(reason ?? "unknown"),
      stack: reason instanceof Error ? (reason.stack ?? "").slice(0, 400) : "",
      appActive: this.appActive,
      overlayWas: this.store.getState().overlay.kind,
    });
  }

  // --- internals ----------------------------------------------------------

  private entryOf(conn: ManagedConnection): Entry | undefined {
    for (const entry of this.entries) if (entry.conn === conn) return entry;
    return undefined;
  }

  /**
   * ⚠️ WE ARE BACK ON SCREEN — DROP EVERY LATCHED «we went away» MARKER, WHICHEVER ONE SET IT.
   *
   * Three of the four fused inputs LATCH: `pagehide` sets {@link pageHidden}, `freeze` sets
   * {@link frozen}, and a Telegram deactivation sets {@link tgActive} false. Each used to be
   * cleared only by its own opposite event, so RETURNING THROUGH A DIFFERENT SIGNAL THAN THE ONE
   * THAT HID US pinned the app INACTIVE for the rest of the session — no reconnect sweeps ever
   * again, and, because `sound/sfx.ts` reads this same signal, `playSfx` early-returning forever:
   * the game plays perfectly, in total silence. Measured against this class, 5 of the 9
   * pin/restore pairs were stuck — pagehide→Telegram-activated, pagehide→resume,
   * TG-deactivated→visibilitychange, TG-deactivated→pageshow, freeze→visibilitychange. Only the
   * pagehide→visibilitychange cell had been closed (2026-08-16), one cell of a whole family.
   *
   * Mismatched pairs are the NORMAL case on iOS, where a Mini App is left and re-entered without
   * any navigation: WebKit fires `pagehide` liberally and `pageshow` almost never, and Telegram's
   * own activation is a separate stream again. The host of a private table is the player who hits
   * it, because sharing the invite («دعوت دوستان از تلگرام» opens Telegram's chat picker; «کپی
   * لینک میز» means switching to a chat to paste) is the one round trip OUT of the app and back
   * that a seated player makes — a guest taps a link and simply arrives.
   *
   * SAFE BY ASYMMETRY: a stale latch can only ever produce a FALSE INACTIVE, never a false active,
   * because `document.visibilityState` is re-read live on every evaluation and still holds a
   * genuinely hidden document inactive. Clearing on any credible return therefore only ever moves
   * the app from stuck-inactive to active.
   *
   * ⚠️ THE ONE JUDGEMENT CALL, STATED RATHER THAN HIDDEN: a `visibilitychange` reporting VISIBLE
   * also drops a Telegram deactivation, and Telegram is the only source that can see a Mini App
   * minimised while its WebView still reports «visible». That costs a wrong ACTIVE only in the
   * narrow case where the document goes hidden→visible while the app stays minimised (Telegram
   * itself backgrounded and foregrounded), and the next real Telegram event re-asserts the truth —
   * against a stale latch that never self-heals at all. A wrong active costs a wasted sweep and a
   * cue nobody hears; a wrong inactive costs every sound for the rest of the session.
   */
  private noteReturn(): void {
    this.pageHidden = false;
    this.frozen = false;
    // A stale deactivation becomes UNKNOWN, never a fabricated activation: only Telegram may
    // assert that its own Mini App is active. `null` simply stops blocking the fusion below.
    if (this.tgActive === false) this.tgActive = null;
  }

  private isRawActive(): boolean {
    const docHidden = typeof document !== "undefined" && document.visibilityState === "hidden";
    return !docHidden && !this.pageHidden && !this.frozen && this.tgActive !== false;
  }

  /** Debounced apply: bursts of signals (the resume path fires several at once)
   *  collapse into a single transition. */
  private evaluate(): void {
    if (this.debounceTimer !== null) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      const active = this.isRawActive();
      const changed = active !== this.appActive;
      const force = this.forceSweep && active;
      this.forceSweep = false;
      if (changed) this.applyActive(active);
      else if (force) this.sweep();
    }, this.debounceMs);
  }

  private applyActive(active: boolean): void {
    this.gen++; // stale cycles/sweeps stop at their next checkpoint
    if (active) {
      this.store.setState({ appActive: true, hiddenAt: null });
      for (const entry of this.entries) {
        if (entry.hiddenSince !== null) {
          entry.hiddenMs += Date.now() - entry.hiddenSince;
          entry.hiddenSince = null;
        }
      }
      this.sweep();
      return;
    }
    this.store.setState({ appActive: false, hiddenAt: this.now() });
    for (const entry of this.entries) {
      if (entry.hiddenSince === null) entry.hiddenSince = Date.now();
      entry.wake?.(); // collapse backoff sleeps — no attempts in the background
      try {
        entry.conn.suspend();
      } catch (error) {
        console.error(`[lifecycle] suspend ${entry.conn.name} failed`, error);
      }
    }
  }

  /** The on-visible health sweep: probe every managed connection, reconnect
   *  the dead ones. */
  private sweep(): void {
    const gen = this.gen;
    for (const entry of this.entries) {
      entry.failed = false; // a fresh visible transition earns a fresh cycle
      void this.checkEntry(entry, gen);
    }
  }

  private async checkEntry(entry: Entry, gen: number): Promise<void> {
    if (entry.expired) return;
    entry.wake?.(); // wake a superseded cycle out of its backoff sleep…
    if (entry.cycle) await entry.cycle; // …and let it unwind before re-probing
    if (gen !== this.gen || !this.appActive || !this.entries.has(entry)) return;
    let alive = false;
    try {
      alive = await entry.conn.probe();
    } catch (error) {
      console.error(`[lifecycle] probe ${entry.conn.name} failed`, error);
    }
    if (gen !== this.gen || !this.entries.has(entry)) return;
    if (alive) {
      // Healthy — clear a stale overlay from a superseded cycle. "failed" is included
      // deliberately: leaving it up over a connection we have just PROVED alive is the
      // dead end the operator hit (its retry button re-probes, finds the socket fine, and
      // used to leave the modal exactly where it was).
      const stale = this.store.getState().overlay.kind;
      if (!entry.conn.silent && (stale === "reconnecting" || stale === "failed")) {
        this.setOverlay({ kind: "none" });
      }
      // …and a stale inline notice (a cycle abandoned by a hidden transition
      // leaves the connection's own UI on "reconnecting").
      this.notifyStatus(entry, "connected");
      return;
    }
    entry.cycle = this.runCycle(entry, gen).finally(() => {
      entry.cycle = null;
    });
    await entry.cycle;
  }

  /**
   * Exponential-backoff reconnect cycle for one dead connection. Keeps auto-retrying for the FULL
   * budget ({@link RECONNECT_WINDOW_MS} + {@link SERVER_GRACE_MARGIN_MS}, deliberately LONGER than
   * the server's 60s drop grace — see RECONNECT_WINDOW_MS) — showing the smooth "reconnecting"
   * state the whole time — and only surfaces "failed" once that budget has really been spent. The
   * seat resolves earlier if the server has released it for good (a reconnect refused →
   * {@link SeatExpiredError} → "seat expired"); a refusal that only means the server has not yet
   * noticed the drop ({@link ReconnectTooEarlyError}) is retried instead.
   */
  private async runCycle(entry: Entry, gen: number): Promise<void> {
    const startedAt = Date.now();
    let attempts = 0;
    let lastError: unknown = null;
    entry.hiddenMs = 0;
    entry.hiddenSince = this.appActive ? null : Date.now();
    entry.recovered = false;
    /**
     * FROZEN TIME IS NOT ACTIVE TIME.
     *
     * `hiddenMs` is driven by visibility events — and a SUSPENDED WebView fires none: iOS stops
     * the JS context between two instructions, so `visibilitychange` never runs, `setTimeout`
     * never fires (including {@link REACQUIRE_TIMEOUT_MS}'s bound), and the pending socket never
     * settles. Everything resumes minutes later as if no time had passed, so the whole gap was
     * charged against a budget that exists to hold retries — observed in production as
     * `attempts=1 elapsedMs=600522 windowMs=135000 hiddenMs=0`, where BOTH the 15s bound and the
     * 135s budget appear violated and neither actually ran.
     *
     * A timer that fails to fire IS the signal. This one ticks every second; a gap far larger
     * than that cannot be scheduler jitter, so the excess is charged as hidden — which is what it
     * was. Guarded on `hiddenSince === null`: a genuinely backgrounded interval is already
     * accounted for, and counting both would double-charge.
     *
     * ⚠️ AND IT IS SETTLED WHEREVER THE BUDGET IS READ, NOT ONLY ON ITS OWN TICK. On a wake the
     * NETWORK callback can run before any timer — prod, 2026-09-23: `attempts=1 elapsedMs=179900
     * hiddenMs=0 lastError=Error: event=error readyState=4`, a page frozen ~3 min whose XHR error
     * ran first, so {@link activeElapsed} read three frozen minutes as retry time and the cycle gave
     * up on the callback that woke it. Idempotent: every call advances `lastTick`, so the interval
     * and the budget checks can never charge the same gap twice.
     */
    let lastTick = Date.now();
    const chargeFreeze = (): void => {
      const now = Date.now();
      const gap = now - lastTick;
      lastTick = now;
      if (gap > FREEZE_GAP_MS && entry.hiddenSince === null) {
        entry.hiddenMs += gap - FREEZE_PROBE_MS;
      }
    };
    const freezeProbe = setInterval(chargeFreeze, FREEZE_PROBE_MS);
    try {
      /** Elapsed time MINUS whatever was spent suspended. A backgrounded WebView cannot
       *  retry, so that time must not be charged against a budget meant to hold retries —
       *  otherwise a phone locked for two minutes returns to an already-exhausted window. */
      const activeElapsed = (): number => {
        chargeFreeze(); // settle a freeze the probe has not ticked over yet — see above
        const hidden =
          entry.hiddenMs + (entry.hiddenSince === null ? 0 : Date.now() - entry.hiddenSince);
        return Date.now() - startedAt - hidden;
      };
      const budgetMs = RECONNECT_WINDOW_MS + SERVER_GRACE_MARGIN_MS;
      for (
        let attempt = 1;
        // `!entry.recovered`: a late reconnect adopted while we slept ends the loop — another
        // attempt would start by closing the room just adopted.
        attempt <= MAX_RECONNECT_ATTEMPTS && activeElapsed() < budgetMs && !entry.recovered;
        attempt++
      ) {
        if (gen !== this.gen || !this.appActive || !this.entries.has(entry)) return;
        // Stay silent while a quick recovery is still plausible — see RECONNECT_NOTICE_DELAY_MS.
        if (!entry.conn.silent && activeElapsed() >= RECONNECT_NOTICE_DELAY_MS) {
          this.setOverlay({ kind: "reconnecting", attempt, maxAttempts: MAX_RECONNECT_ATTEMPTS });
        }
        this.notifyStatus(entry, "reconnecting");
        attempts = attempt;
        try {
          await entry.conn.reconnect();
          // Unregistered while the attempt was in flight (the player exited):
          // never touch the overlay for a dead entry — a NEXT game would inherit it.
          if (!this.entries.has(entry)) return;
          entry.failed = false;
          if (!entry.conn.silent) this.setOverlay({ kind: "none" });
          this.notifyStatus(entry, "connected");
          return;
        } catch (error) {
          if (!this.entries.has(entry)) return; // exited mid-attempt — stay silent
          // An EARLIER attempt's reconnect landed and was adopted while this one was out — its
          // failure (typically the refusal of the token that reclaim just rotated) is moot, and
          // `reportRecovered` has already settled the UI.
          if (entry.recovered) return;
          if (error instanceof SeatExpiredError) {
            entry.expired = true;
            console.error(`[lifecycle] ${entry.conn.name} seat expired`, error);
            if (!entry.conn.silent)
              this.setOverlay({ kind: "seatExpired", preStart: error.preStart });
            this.notifyStatus(entry, "failed");
            return;
          }
          lastError = error;
          console.error(
            `[lifecycle] reconnect ${entry.conn.name} attempt ${attempt} failed`,
            error,
          );
        }
        // Back off before the next attempt — but never sleep PAST the budget's deadline, so
        // "failed" fires as close to the real window as the schedule allows.
        const remaining = budgetMs - activeElapsed();
        if (remaining <= 0) break;
        const backoff = backoffDelayMs(attempt, this.random);
        // «Not yet» is retried on ITS schedule: the release it is waiting for is seconds away,
        // and by the third attempt the backoff alone would already sleep past it.
        const wait =
          lastError instanceof ReconnectTooEarlyError
            ? Math.min(backoff, lastError.retryInMs)
            : backoff;
        await this.backoffSleep(entry, Math.min(wait, remaining));
      }
      if (entry.recovered) return; // the loop ended on an adopted late reconnect — not a give-up
      if (gen !== this.gen || !this.entries.has(entry)) return;
      entry.failed = true;
      // WHY the cycle gave up, in the terms that discriminate the competing explanations:
      // did the wall-clock window really elapse (and how much of it was spent backgrounded),
      // or did we simply run out of attempts?
      reportClientFault("reconnect-gave-up", {
        conn: entry.conn.name,
        attempts,
        elapsedMs: Date.now() - startedAt,
        activeMs: activeElapsed(),
        windowMs: budgetMs,
        hiddenMs: entry.hiddenMs,
        reason: activeElapsed() >= budgetMs ? "budget" : "attempts",
        lastError: describeConnectionError(lastError),
        appActive: this.appActive,
      });
      if (!entry.conn.silent) this.setOverlay({ kind: "failed" });
      this.notifyStatus(entry, "failed");
    } finally {
      clearInterval(freezeProbe);
    }
  }

  /** Report a connection's own reconnect state — a listener throwing must never
   *  break the cycle (it drives UI, not the reconnect itself). */
  private notifyStatus(entry: Entry, status: ManagedConnectionStatus): void {
    try {
      entry.conn.onStatus?.(status);
    } catch (error) {
      console.error(`[lifecycle] onStatus ${entry.conn.name} failed`, error);
    }
  }

  /** A sleep the lifecycle can collapse (hidden transition / fresh sweep). */
  private backoffSleep(entry: Entry, ms: number): Promise<void> {
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        entry.wake = null;
        resolve();
      }, ms);
      entry.wake = () => {
        clearTimeout(timer);
        entry.wake = null;
        resolve();
      };
    });
  }

  private setOverlay(overlay: OverlayState): void {
    this.store.setState({ overlay });
  }
}

/** The app-wide singleton every real connector registers with. Inert until
 *  `start()` is called (main.tsx) — tests that import connectors never see it
 *  react to anything. */
export const lifecycle = new ConnectionLifecycle();

/** True while the app-root `ConnectionOverlay` owns the screen (reconnecting /
 *  failed / seat-expired). The game containers gate their OWN full-screen error
 *  surface on this being false: a screen-lock socket drop fires BOTH the
 *  lifecycle reconnect (Persian overlay, z-70) AND the store's `onError` with a
 *  raw Colyseus reason (e.g. "Error undefined"); rendering the container error
 *  screen (z-auto) behind the overlay showed that raw English peeking through the
 *  60%-opacity scrim (user report 2026-07-10). While the overlay is up it owns
 *  recovery end-to-end (reconnect → clears via the re-emitted state; terminal →
 *  the overlay's own Persian failed/seat-expired), so the container error is pure
 *  duplication. Server-sent errors while NOT reconnecting still render normally. */
export function useOverlayActive(): boolean {
  return useStore(lifecycle.store, (s) => s.overlay.kind !== "none");
}

/* ------------------------------------------------------------------ *
 * manageRoom — the shared reconnect glue every Colyseus connector uses
 * ------------------------------------------------------------------ */

/** The structural slice of a colyseus.js `Room` the manager drives. */
export interface RoomLike extends ProbeableRoom {
  onLeave(callback: (code: number) => void): unknown;
  removeAllListeners?: () => void;
  leave(consented?: boolean): Promise<unknown>;
}

export interface ManagedRoom<R extends RoomLike> {
  /** The live room (swapped on every successful reconnect). */
  current(): R;
  /** Throw-safe send to the CURRENT room. */
  send(type: string, message?: unknown): void;
  /** Deliberate exit: unregister + fire-and-forget close. NEVER throws or
   *  blocks — every leave path must navigate immediately. */
  leave(): void;
  /** Close it the way a DROPPED socket closes: unregister, drop the listeners, and a NON-consented
   *  close — so a server that holds a seat keeps it for its grace and charges nothing. For a
   *  connection nobody chose to leave (it landed after the player had gone). Never throws. */
  drop(): void;
  /** Stop managing without closing (the game reached its terminal state — a
   *  finished match must never auto-rejoin). */
  release(): void;
  /** Manual retry after the cycle exhausted (`onStatus: "failed"`): report the
   *  connection dead again so the lifecycle starts a fresh backoff cycle (the
   *  chat surfaces' «تلاش دوباره»). No-op after leave/release. */
  retry(): void;
  /**
   * Resolve `true` as soon as the connection is (or comes) UP — at once when it already is, or when
   * the lifecycle's reconnect adopts a fresh room — and `false` after `ms` without one, or at once
   * once the connection is over (left / released). For a frame that only means something on a live
   * socket (card-a5 item 1: «خروج»'s `leave-forever`, confirmed while the socket was down).
   */
  whenLive(ms: number): Promise<boolean>;
}

/**
 * Wraps one live Colyseus room in the lifecycle contract: registers the
 * probe/reconnect/suspend hooks, re-wires handlers onto every replacement
 * room, reports non-consented drops (so a wifi blip mid-game reconnects
 * without waiting for a visibility change), and guarantees the exit path can
 * never throw. Used by every connector (solo, hearts, online, presence, and
 * the dm/table-chat sockets).
 */
export function manageRoom<R extends RoomLike>(opts: {
  name: string;
  /** Injected in tests; the app uses the singleton. */
  registry?: ConnectionRegistry | undefined;
  initial: R;
  /** Acquire the replacement room: plain rejoin (solo/presence) or
   *  `client.reconnect(token)` (online). Reject {@link SeatExpiredError} to
   *  stop the retry cycle. */
  reacquire: (previous: R) => Promise<R>;
  /** (Re)attach the connector's message handlers to a (fresh) room. */
  wire: (room: R) => void;
  /** Background connection — never drives the overlay. */
  silent?: boolean;
  /** Per-connection status observer (see {@link ManagedConnection.onStatus}) —
   *  how a `silent` connection with its own UI (chat) surfaces reconnect state. */
  onStatus?: (status: ManagedConnectionStatus) => void;
  /** Runs after every successful reconnect (reset transient store state /
   *  re-send the current activity); the server re-emits full state after it. */
  onReconnected?: () => void;
  /** How long ONE reacquire attempt may run before it is abandoned. Defaults to
   *  {@link REACQUIRE_TIMEOUT_MS}, which is sized for the fresh-join reservation window; a
   *  connector whose reservation genuinely lives longer passes its own — see
   *  {@link ONLINE_REACQUIRE_TIMEOUT_MS}. */
  reacquireTimeoutMs?: number;
  /** Wait, at most this long, for the torn-down socket's close to LAND before reacquiring — for a
   *  connector whose reacquire needs the SERVER to have let go of the old socket first (the online
   *  table's `client.reconnect`; see {@link ONLINE_CLOSE_WAIT_MS}). Omitted: reacquire at once,
   *  which is right for every connector that takes a FRESH seat. */
  closeWaitMs?: number;
}): ManagedRoom<R> {
  const registry = opts.registry ?? lifecycle;
  let room = opts.initial;
  /** True once this connection is deliberately over (left / terminal) — the
   *  room's own close event must no longer trigger reconnection. */
  let done = false;
  /**
   * Whether {@link room} is a connection we believe is UP. False from the moment a reconnect tears
   * the old one down (or its socket drops) until one is adopted — and a reconnect that lands late
   * is adopted only while it is false. True means a newer attempt already won, so the late one is
   * a duplicate.
   */
  let live = true;
  /** We closed it ourselves (`leave` / `drop`) — as opposed to `release`, which keeps the socket. */
  let closedHere = false;
  /** Callers of `whenLive` waiting for the connection to come back up. */
  const liveWaiters = new Set<(up: boolean) => void>();
  const settleLiveWaiters = (up: boolean): void => {
    for (const waiter of [...liveWaiters]) waiter(up);
  };

  const adopt = (next: R): void => {
    room = next;
    live = true;
    wire(room);
    opts.onReconnected?.();
    settleLiveWaiters(true);
  };

  /**
   * ADOPT, DON'T CLOSE: a reconnect whose attempt we had already abandoned has landed.
   *
   * It is the connection we were waiting for, arriving a moment after the path came back — for the
   * online table it has ALREADY reclaimed the seat server-side and rotated the token, so closing it
   * (what the disposer used to do) handed the chair to a bot a second time and left every later
   * attempt holding a token the server no longer honours. Measured on prod 2026-09-26 22:54:51
   * (`grep -n 'ADOPT, DON.T CLOSE' docs/journal/2026-09.md`). Kept whenever nothing else holds the
   * connection — mid-cycle, after the cycle gave up, even after a refusal painted «seat expired».
   * Closed (non-consented, like every teardown) only when it is a duplicate or the player left.
   */
  const adoptLate = (late: R): void => {
    if (done || live) {
      teardownRoom(late);
      return;
    }
    adopt(late);
    registry.reportRecovered(managed);
  };

  const managed: ManagedConnection = {
    name: opts.name,
    ...(opts.silent ? { silent: true } : {}),
    ...(opts.onStatus ? { onStatus: opts.onStatus } : {}),
    probe: () => probeRoom(room),
    reconnect: async () => {
      const before = room;
      live = false;
      const closed = teardownRoom(before);
      if (opts.closeWaitMs) {
        // DON'T RACE OUR OWN CLOSE — see ONLINE_CLOSE_WAIT_MS. And re-ask what may have changed
        // while it landed: the player left (nothing may be sent for a table they walked away
        // from), or an earlier attempt's late reclaim was adopted (the connection is already
        // back; another request would only be refused or duplicate it).
        await settleWithin(closed, opts.closeWaitMs);
        if (done) throw new Error(`${opts.name}: closed during reconnect`);
        if (room !== before) return;
      }
      // BOUNDED: an attempt that never completes is abandoned so the loop can retry — and if it
      // resolves afterwards, `adoptLate` decides whether it is the connection or a duplicate. See
      // boundedReacquire / REACQUIRE_TIMEOUT_MS.
      let next: R;
      try {
        next = await boundedReacquire(
          opts.reacquire(before),
          opts.name,
          adoptLate,
          opts.reacquireTimeoutMs ?? REACQUIRE_TIMEOUT_MS,
        );
      } catch (error) {
        // An earlier attempt's late reconnect was adopted while this one was out: the connection
        // is up, and this attempt's failure — typically the refusal of the token that reclaim just
        // rotated — is moot.
        if (room !== before) return;
        throw error;
      }
      if (done) {
        // The player exited while the reconnect was in flight — don't adopt
        // the fresh room (nobody would ever leave it): close it and fail the
        // attempt. The lifecycle stays silent for unregistered entries.
        //
        // NON-consented, like `adoptLate`'s close of the same late arrival: the reconnect overlay's
        // «خروج» is not «خروج بدون بازگشت», and a CONSENTED close is what the online table reads
        // as a deliberate leave — a bot at once, and a claimable public شطرنج/تخته نرد.
        teardownRoom(next);
        throw new Error(`${opts.name}: closed during reconnect`);
      }
      if (room !== before) {
        teardownRoom(next); // a late arrival already brought the connection back — a duplicate
        return;
      }
      adopt(next);
    },
    suspend: () => {
      // No teardown — a short lock must not cost the seat. The next visible
      // probe decides whether the socket survived.
    },
  };

  function wire(r: R): void {
    opts.wire(r);
    // A permanent no-op pong listener keeps colyseus.js quiet when a probe's
    // pong lands after its deadline (the transient probe listener is gone).
    try {
      r.onMessage("pong", () => undefined);
    } catch {
      // a test double without full onMessage support — probes still work
    }
    r.onLeave(() => {
      // The socket dropped underneath us (screen lock kill, wifi blip). A
      // deliberate leave/terminal release sets `done` FIRST, so only real
      // drops reconnect. While hidden the lifecycle ignores this and the
      // resume sweep handles it instead.
      live = false; // a late reclaim landing from now on is the seat's socket — keep it
      if (!done) registry.reportDead(managed);
    });
  }

  wire(room);
  const unregister = registry.register(managed);

  return {
    current: () => room,
    send: (type, message) => safeSend(room, type, message),
    leave: () => {
      done = true;
      closedHere = true;
      unregister();
      safeRoomLeave(room);
      settleLiveWaiters(false);
    },
    drop: () => {
      done = true;
      closedHere = true;
      unregister();
      teardownRoom(room);
      settleLiveWaiters(false);
    },
    release: () => {
      done = true;
      unregister();
      settleLiveWaiters(false);
    },
    retry: () => {
      if (!done) registry.reportDead(managed);
    },
    whenLive: (ms) => {
      if (closedHere) return Promise.resolve(false);
      // A RELEASED connection (the match is over) still has its socket — it is just no longer
      // reconnected. It is as live as it last was, and nothing will bring it back if it is not.
      if (live || done) return Promise.resolve(live);
      return new Promise<boolean>((resolve) => {
        const settle = (up: boolean): void => {
          liveWaiters.delete(settle);
          clearTimeout(timer);
          resolve(up);
        };
        liveWaiters.add(settle);
        const timer = setTimeout(() => settle(false), ms);
      });
    },
  };
}

/* ------------------------------------------------------------------ *
 * Global error routing
 * ------------------------------------------------------------------ */

/**
 * Last-resort crash guard (main.tsx): an unhandled JS error inside Telegram's
 * WebView surfaces as iOS's "unexpected error" sheet and kills the app. While a
 * game view is active we route it into the ConnectionOverlay's failed state
 * (retry + exit both keep working) instead. Always logged — never swallowed.
 */
export function installGlobalErrorRouting(opts: {
  isGameActive: () => boolean;
  lifecycle?: ConnectionLifecycle;
}): () => void {
  const target = opts.lifecycle ?? lifecycle;
  const route = (event: Event, detail: unknown): void => {
    console.error("[app] uncaught", detail);
    if (!opts.isGameActive()) return;
    event.preventDefault(); // stop the WebView's fatal default path
    target.reportCrash(detail);
  };
  const onError = (event: Event): void => {
    const e = event as ErrorEvent;
    route(event, e.error ?? e.message ?? "unknown error");
  };
  const onRejection = (event: Event): void => {
    route(event, (event as PromiseRejectionEvent).reason ?? "unhandled rejection");
  };
  /**
   * A RESOURCE that failed to load — a stylesheet, a lazy chunk, an image.
   *
   * ⚠️ THIS NEEDS `capture: true` AND THAT IS THE WHOLE POINT. A resource error fires ON THE
   * ELEMENT and does NOT bubble, so the plain `window.addEventListener("error", …)` above — the
   * one listener meant to catch client faults — could never see one. A stylesheet failing to load
   * was therefore completely invisible: on 2026-08-01 a player's table rendered its cards and
   * avatars with no felt, header, score or controls and one seat adrift, and NOTHING was captured.
   *
   * It REPORTS but does not route: a dead image is not a reason to put a connection-failure
   * overlay in front of someone, and `preventDefault()` here would suppress nothing useful. The
   * `target === window` guard keeps JS errors (which are dispatched at window and so also reach a
   * capture listener on it) from being double-reported as resources.
   */
  const onResourceError = (event: Event): void => {
    const el = event.target as (Element & { src?: string; href?: string }) | null;
    if (!el || el === (window as unknown as Element) || typeof el.tagName !== "string") return;
    const url = el.src ?? el.href;
    if (!url) return;
    // An element marked as one that will be RETRIED is not news: a severed transfer the retry
    // rescues is the normal weather on these networks, and reporting each attempt would multiply
    // one lost picture into five. Only the terminal failure is reported — the same call
    // `card-faces.ts` makes for the SVG chunk. (Deck faces are preloaded off-DOM since 2026-10-20 and
    // report their own terminal failure: `deck-assets.ts`.)
    if (el.hasAttribute("data-face-retry")) return;
    reportClientFault("resource-failed", { tag: el.tagName.toLowerCase(), url });
  };

  window.addEventListener("error", onError);
  window.addEventListener("error", onResourceError, true);
  window.addEventListener("unhandledrejection", onRejection);
  return () => {
    window.removeEventListener("error", onError);
    window.removeEventListener("error", onResourceError, true);
    window.removeEventListener("unhandledrejection", onRejection);
  };
}
